/**
 * Recording List Component
 * 
 * Part of Native Voice Capture feature (Epic 13)
 * Task 2.10: Web Recording Management UI
 * 
 * Displays a list of recordings with filtering, sorting, and search.
 */

import React, { useState, useMemo } from 'react';
import { RecordingListProps, Recording, SortField, SortOrder, StatusFilter, ViewMode } from './types';
import { RecordingCard } from './RecordingCard';
import styles from './RecordingList.module.css';

export const RecordingList: React.FC<RecordingListProps> = ({
  recordings = [],
  loading = false,
  error = null,
  onRecordingSelect,
  onDelete,
  onDownload,
  viewMode: initialViewMode = 'grid',
  sortBy: initialSortBy = 'date',
  sortOrder: initialSortOrder = 'desc',
  filterStatus: initialFilterStatus = 'all',
  searchQuery: initialSearchQuery = '',
}) => {
  const [selectedRecording, setSelectedRecording] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [sortBy, setSortBy] = useState<SortField>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>(initialFilterStatus);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

  // Filter and sort recordings
  const filteredAndSortedRecordings = useMemo(() => {
    let result = [...recordings];

    // Filter by status
    if (filterStatus !== 'all') {
      result = result.filter((r) => r.status === filterStatus);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.title?.toLowerCase().includes(query) ||
          r.id.toLowerCase().includes(query) ||
          r.transcriptText?.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime();
          break;
        case 'duration':
          comparison = a.duration - b.duration;
          break;
        case 'title':
          comparison = (a.title || a.id).localeCompare(b.title || b.id);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [recordings, filterStatus, searchQuery, sortBy, sortOrder]);

  const handleRecordingSelect = (recording: Recording) => {
    setSelectedRecording(recording.id);
    if (onRecordingSelect) {
      onRecordingSelect(recording);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} aria-label="Loading recordings"></div>
          <p>Loading recordings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error} role="alert">
          <h3>Error Loading Recordings</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>My Recordings</h2>
        <div className={styles.stats}>
          {recordings.length} recording{recordings.length !== 1 ? 's' : ''}
          {filterStatus !== 'all' && ` (${filteredAndSortedRecordings.length} shown)`}
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        {/* Search */}
        <div className={styles.searchContainer}>
          <input
            type="search"
            placeholder="Search recordings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            aria-label="Search recordings"
          />
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <label htmlFor="status-filter" className={styles.label}>
            Status:
          </label>
          <select
            id="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
            className={styles.select}
          >
            <option value="all">All</option>
            <option value="uploaded">Uploaded</option>
            <option value="processing">Processing</option>
            <option value="transcribed">Ready</option>
            <option value="failed">Failed</option>
          </select>

          <label htmlFor="sort-by" className={styles.label}>
            Sort by:
          </label>
          <select
            id="sort-by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortField)}
            className={styles.select}
          >
            <option value="date">Date</option>
            <option value="duration">Duration</option>
            <option value="title">Title</option>
            <option value="size">Size</option>
          </select>

          <button
            onClick={toggleSortOrder}
            className={styles.sortButton}
            aria-label={`Sort order: ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        {/* View Mode */}
        <div className={styles.viewModeToggle}>
          <button
            onClick={() => setViewMode('grid')}
            className={`${styles.viewButton} ${viewMode === 'grid' ? styles.active : ''}`}
            aria-label="Grid view"
            title="Grid view"
          >
            ⊞
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`${styles.viewButton} ${viewMode === 'list' ? styles.active : ''}`}
            aria-label="List view"
            title="List view"
          >
            ≡
          </button>
        </div>
      </div>

      {/* Recordings Grid/List */}
      {filteredAndSortedRecordings.length === 0 ? (
        <div className={styles.empty}>
          <p>
            {searchQuery || filterStatus !== 'all'
              ? 'No recordings match your filters.'
              : 'No recordings yet. Start by creating your first recording!'}
          </p>
        </div>
      ) : (
        <div className={`${styles.grid} ${styles[viewMode]}`}>
          {filteredAndSortedRecordings.map((recording) => (
            <RecordingCard
              key={recording.id}
              recording={recording}
              onSelect={() => handleRecordingSelect(recording)}
              onDelete={onDelete ? () => onDelete(recording.id) : undefined}
              onDownload={onDownload ? (type) => onDownload(recording.id, type) : undefined}
              selected={selectedRecording === recording.id}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

